// src/components/app-hub/CategoryAppGrid.tsx
import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as dndCore from '@dnd-kit/core';
import * as dndSortable from '@dnd-kit/sortable';
import * as dndUtils from '@dnd-kit/utilities';
import type { AppRegistryEntry, AppCategory } from '../../types/appRegistry';
import { APP_CATEGORY_LABELS } from '../../types/appRegistry';
import type { LucideIcon } from 'lucide-react';
import {
  Heart, School, Book, Calendar, Image, Store, Lightbulb, Database,
  BookOpen, BookText, Users, ClipboardList, TrendingUp, DollarSign,
  BarChart3, Search, Package, FileText, Layers,
} from 'lucide-react';

/** Map Firestore app IDs to lucide-react icon components */
const APP_ICON_MAP: Record<string, LucideIcon> = {
  'good-things': Heart,
  'plms': School,
  'sop': Book,
  'dayaway': Calendar,
  'ideogram': Image,
  'lms': Store,
  'idea-machine': Lightbulb,
  'pmt2': Database,
  'ai-devquest': BookOpen,
  'glossary': BookText,
  'employee-directory': Users,
  'ppc-farm': ClipboardList,
  'farming-results': TrendingUp,
  'primary-ledger': Layers,
  'asin-sales-history': BarChart3,
  'sb-search-terms': Search,
  'amazon-index-checker': Search,
  'product-manager': Package,
  'days-of-cover': DollarSign,
  'smart-writer': FileText,
};

interface Props {
  category: AppCategory;
  apps: AppRegistryEntry[];
  orderedIds: string[];
  onReorder: (fromIndex: number, toIndex: number) => void;
  onHideApp?: (appId: string) => void;
}

/** Check if a URL belongs to JLS Apps (same-window navigation) */
function isJlsAppsUrl(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin);
    const host = parsed.hostname.toLowerCase();
    return host === 'jlsapps.com' || host.endsWith('.jlsapps.com')
      || host === 'jls-apps.netlify.app' || host.endsWith('.jls-apps.netlify.app');
  } catch {
    return false;
  }
}

/** Darken a hex color by a percentage (0-1) */
function darkenColor(hex: string, amount: number): string {
  const c = hex.replace('#', '');
  const num = parseInt(c, 16);
  const r = Math.max(0, Math.floor(((num >> 16) & 0xff) * (1 - amount)));
  const g = Math.max(0, Math.floor(((num >> 8) & 0xff) * (1 - amount)));
  const b = Math.max(0, Math.floor((num & 0xff) * (1 - amount)));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

function SortableCard({
  id,
  app,
  dragActive,
  onHideApp,
}: {
  id: string;
  app: AppRegistryEntry;
  dragActive?: boolean;
  onHideApp?: (appId: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    dndSortable.useSortable({ id });
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = useCallback(() => {
    setHovered(true);
    tooltipTimer.current = setTimeout(() => setShowTooltip(true), 400);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHovered(false);
    setShowTooltip(false);
    if (tooltipTimer.current) {
      clearTimeout(tooltipTimer.current);
      tooltipTimer.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (tooltipTimer.current) clearTimeout(tooltipTimer.current);
    };
  }, []);

  const style: React.CSSProperties = {
    transform: dndUtils.CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleClick = () => {
    if (dragActive) return;
    if (app.type === 'internal' || isJlsAppsUrl(app.url)) {
      if (app.url.startsWith('/')) {
        navigate(app.url);
      } else {
        window.location.href = app.url;
      }
    } else {
      window.open(app.url, '_blank', 'noopener,noreferrer');
    }
  };

  const accentColor = app.iconColor || '#3B82F6';
  const gradientDark = darkenColor(accentColor, 0.35);
  const isExternal = app.type === 'external' && !isJlsAppsUrl(app.url);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      data-testid={`app-card-${id}`}
      className={`
        relative rounded-xl cursor-pointer
        bg-white dark:bg-gray-800/90
        border border-gray-200/80 dark:border-gray-700/60
        shadow-sm
        transition-all duration-200 ease-out
        ${hovered && !dragActive ? 'shadow-xl scale-[1.03] -translate-y-0.5 border-gray-300 dark:border-gray-500/60' : ''}
      `}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Gradient banner header */}
      <div
        className="relative overflow-hidden rounded-t-xl px-4 pt-4 pb-3 flex items-center gap-3"
        style={{
          background: `linear-gradient(135deg, ${accentColor}, ${gradientDark})`,
        }}
      >
        {/* Decorative circle in top-right */}
        <div
          className="absolute -top-3 -right-3 w-16 h-16 rounded-full opacity-20"
          style={{ backgroundColor: '#fff' }}
        />

        {/* Hide button */}
        {onHideApp && hovered && !dragActive && (
          <button
            onClick={(e) => { e.stopPropagation(); onHideApp(id); }}
            className="absolute top-1.5 right-1.5 z-20 w-5 h-5 rounded-full bg-black/25 hover:bg-black/50 flex items-center justify-center transition-colors backdrop-blur-sm"
            title="Hide from dashboard"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 text-white/80 hover:text-white">
              <path fillRule="evenodd" d="M3.28 2.22a.75.75 0 00-1.06 1.06l14.5 14.5a.75.75 0 101.06-1.06l-1.745-1.745a10.029 10.029 0 003.3-4.38 1.651 1.651 0 000-1.185A10.004 10.004 0 009.999 3a9.956 9.956 0 00-4.744 1.194L3.28 2.22zM7.752 6.69l1.092 1.092a2.5 2.5 0 013.374 3.373l1.092 1.092a4 4 0 00-5.558-5.558z" clipRule="evenodd" />
              <path d="M10.748 13.93l2.523 2.523A9.987 9.987 0 0110 17c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 012.838-4.826l2.457 2.457A4 4 0 0010.748 13.93z" />
            </svg>
          </button>
        )}

        {/* App icon — lucide component preferred, then custom/favicon image */}
        {(() => {
          const IconComponent = APP_ICON_MAP[app.id];
          if (IconComponent) {
            return (
              <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <IconComponent className="h-5 w-5 text-white" />
              </div>
            );
          }
          if (app.customIconUrl || app.faviconUrl) {
            return (
              <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <img src={(app.customIconUrl || app.faviconUrl)!} alt="" className="h-5 w-5 rounded" />
              </div>
            );
          }
          return null;
        })()}

        {/* App name as the hero element */}
        <h3
          className="text-white font-bold text-sm leading-tight tracking-wide truncate relative z-10"
          style={{ textShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
        >
          {app.name}
        </h3>

        {/* External badge */}
        {isExternal && (
          <span className="ml-auto flex-shrink-0 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/20 text-white/90 relative z-10">
            Ext
          </span>
        )}
      </div>

      {/* Screenshot preview */}
      {app.screenshotUrl && (
        <div className="px-2 pt-2">
          <img
            src={app.screenshotUrl}
            alt=""
            className="w-full h-24 object-cover object-top rounded border border-gray-100 dark:border-gray-700/50"
            loading="lazy"
          />
        </div>
      )}

      {/* Card body */}
      <div className="px-4 pt-3 pb-4">
        <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed">
          {app.description}
        </p>

        {/* Hover footer */}
        <div
          className={`
            mt-3 pt-2 border-t border-gray-100 dark:border-gray-700/50
            flex items-center justify-between
            transition-opacity duration-200
            ${hovered && !dragActive ? 'opacity-100' : 'opacity-0'}
          `}
        >
          {app.submittedBy && (
            <span className="text-[10px] text-gray-400 dark:text-gray-500 truncate">
              by {app.submittedBy.split('@')[0]}
            </span>
          )}
          <span
            className="text-[10px] font-medium ml-auto"
            style={{ color: accentColor }}
          >
            Open &rarr;
          </span>
        </div>
      </div>

      {/* Hover popup — large screenshot preview or full description */}
      {showTooltip && !dragActive && (
        app.screenshotUrl ? (
          <div
            className="absolute left-1/2 -translate-x-1/2 bottom-full mb-3 z-50
              pointer-events-none animate-in fade-in duration-150"
          >
            <div className="w-[480px] rounded-lg shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
              <img src={app.screenshotUrl} alt={app.name} className="w-full" loading="eager" />
            </div>
            {/* Arrow */}
            <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0
              border-l-[6px] border-r-[6px] border-t-[6px]
              border-transparent border-t-white dark:border-t-gray-800" />
          </div>
        ) : app.description && app.description.length > 60 ? (
          <div
            className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50
              max-w-xs w-max px-3 py-2 rounded-lg shadow-lg
              bg-gray-900 dark:bg-gray-700 text-white text-xs leading-relaxed
              pointer-events-none animate-in fade-in duration-150"
          >
            {app.description}
            <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
          </div>
        ) : null
      )}
    </div>
  );
}

export default function CategoryAppGrid({ category, apps, orderedIds, onReorder, onHideApp }: Props) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sortedApps = useMemo(() => {
    const orderMap = new Map(orderedIds.map((id, i) => [id, i]));
    return [...apps].sort((a, b) => {
      const ai = orderMap.get(a.id) ?? Infinity;
      const bi = orderMap.get(b.id) ?? Infinity;
      return ai - bi;
    });
  }, [apps, orderedIds]);

  const sensors = dndCore.useSensors(
    dndCore.useSensor(dndCore.PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragStart = (event: dndCore.DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: dndCore.DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const ids = sortedApps.map((a) => a.id);
    const fromIndex = ids.indexOf(String(active.id));
    const toIndex = ids.indexOf(String(over.id));
    if (fromIndex !== -1 && toIndex !== -1) {
      onReorder(fromIndex, toIndex);
    }
  };

  if (apps.length === 0) return null;

  return (
    <section className="mb-10">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        {APP_CATEGORY_LABELS[category]}
      </h2>
      <dndCore.DndContext
        sensors={sensors}
        collisionDetection={dndCore.closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <dndSortable.SortableContext
          items={sortedApps.map((a) => a.id)}
          strategy={dndSortable.rectSortingStrategy}
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {sortedApps.map((app) => (
              <SortableCard
                key={app.id}
                id={app.id}
                app={app}
                dragActive={activeId !== null}
                onHideApp={onHideApp}
              />
            ))}
          </div>
        </dndSortable.SortableContext>
      </dndCore.DndContext>
    </section>
  );
}
